import { h } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { USER_CLICK, USER_FULLSCREEN, USER_KEYBOARD, USER_PIP, USER_SET_FOCUS } from '../../../../../types';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { isEscapeKeyboard, isValidateKeyboard } from '../../../../common/utils/keyboard';
import { connect, withBreakPoints } from '../../../hoc';
import { FULLSCREEN_DIALOG_LAYER, PIP_DIALOG_LAYER } from '../../../theme/constants';
import { Button } from '../../common';
import { EXP_CONTROL_CONTAINER_STYLES, EXP_CONTROL_ICON_STYLES } from './styles';
import TracksMenu from './tracks/TracksMenu';
import Volume from './volume/Volume';

function ExpControl({
  isAd,
  isDAI,
  isTv,
  volume,
  muted,
  subtitlesAvailable,
  subtitleSelected,
  audiosAvailable,
  audioSelected,
  medium,
  extraSmall,
  small,
  ios,
  isFullscreen,
  pipSupported,
  isEnterToExploreActive
}) {
  const player = usePlayerContext();
  const setStyle = useStyles();
  const fullScreenIconRef = useRef(null);

  const { size: iconSize } = setStyle(EXP_CONTROL_ICON_STYLES);

  const handleFullScreenClick = useCallback((e) => {
    const isKeyboardEvent = e instanceof KeyboardEvent;
    const isValidateKey = isValidateKeyboard(e);
    if (isKeyboardEvent && !isValidateKey && !isEscapeKeyboard(e)) {
      return;
    }

    player.userEvents$.next({
      action: USER_FULLSCREEN,
      value: !isFullscreen,
      source: isKeyboardEvent ? USER_KEYBOARD : USER_CLICK
    });

    player.fullscreen(!isFullscreen);

    // Triggers "enter-to-explore" focusTrap on fullscreen
    if (!isFullscreen) {
      player.userEvents$.next({
        action: USER_SET_FOCUS,
        payload: { element: fullScreenIconRef.current }
      });
    }
  }, [isFullscreen]);

  return (
    <div style={setStyle(EXP_CONTROL_CONTAINER_STYLES)}>
      <TracksMenu /* AudioSubtitles Button */
        hidden={(isAd && !isDAI) || (!subtitlesAvailable.length && audiosAvailable.length < 2)}
        subtitlesAvailable={subtitlesAvailable}
        subtitleSelected={subtitleSelected}
        audiosAvailable={audiosAvailable}
        audioSelected={audioSelected}
        small={small}
        name="btn-tracks"
        disableFocus={false}
        ariaLabel="Audio et sous-titres"
        ariaHidden="false"
        isTv={isTv}
      />

      <Volume
        volume={volume}
        muted={muted}
        medium={medium}
        extraSmall={extraSmall}
        small={small}
        name="btn-volume"
        disableFocus={!isEnterToExploreActive}
        ariaLabel={`${muted ? 'volume désactivé' : 'volume activé'} raccourci touche m`}
        ariaHidden="false"
      />

      <Button
        hidden={!pipSupported || (ios && !pipSupported) || isAd || isTv}
        label="Lecteur réduit (i)"
        onClick={() => {
          player.userEvents$.next({
            action: USER_PIP,
            value: true,
            source: USER_CLICK
          });
          player.requestPIP(true);
        }}
        xs
        name="btn-pip"
        type="icon"
        icon="pip"
        disableFocus
        zIndex={PIP_DIALOG_LAYER}
        size={iconSize}
      />

      <Button
        hidden={isTv}
        label={`${isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} (f)`}
        ariaLabel={`${isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'} raccourci touche f`}
        ariaHidden="false"
        iconRef={fullScreenIconRef}
        onClick={handleFullScreenClick}
        last
        xs
        name="btn-fullscreen"
        disableFocus={false}
        zIndex={FULLSCREEN_DIALOG_LAYER}
        type="icon"
        icon={isFullscreen ? 'fullscreen-on' : 'fullscreen-off'}
        size={iconSize}
      />
    </div>
  );
}

const selector = ({
  ui,
  media,
  volume,
  systemInfo
}) => {
  const { isFullscreen, isEnterToExploreActive } = ui;
  const { pipSupported } = systemInfo;
  const {
    level,
    muted
  } = volume;
  const {
    subtitlesAvailable,
    subtitleSelected,
    audiosAvailable,
    audioSelected,
    isAd,
    isDAI,
    isTv
  } = media;

  return ({
    isAd,
    isDAI,
    isTv,
    volume: level,
    muted,
    subtitlesAvailable,
    subtitleSelected,
    audiosAvailable,
    audioSelected,
    isFullscreen,
    pipSupported,
    isEnterToExploreActive
  });
};

export default withBreakPoints(connect(selector)(ExpControl));
